import useSWR from 'swr';

import { fetchFenixOrgChart } from '../../clients/fenixClient';

const withCompanyUrls = (nodes, rootUrl) =>
    nodes.map((node) =>
        node.is_person || !node.id || !rootUrl
            ? node
            : { ...node, companyUrl: `${rootUrl}company/${node.id}/` }
    );

const toCountryTag = (code) => ({
    label: code.toUpperCase(),
    code: code.toLowerCase(),
});

const toIndividualCard = (individual, rootUrl) => {
    const props = individual.properties || {};

    return {
        primaryKey_: props.primaryKey_,
        name: props.name,
        keyRole: props.key_role,
        companyNames: props.companyNames || [],
        politicalAffiliation: props.politicalAffiliation || [],
        sectors: props.sectors || [],
        sanctions: props.sanctions ? [props.sanctions] : [],
        countries: (props.countryIsoCode || [])
            .filter(Boolean)
            .map(toCountryTag),
        image:
            typeof individual.src === 'string' ? { src: individual.src } : null,
        url:
            props.url ||
            (props.primaryKey_ && rootUrl
                ? `${rootUrl}individual/${props.primaryKey_}/`
                : undefined),
    };
};

const individualsFromChart = (nodes, rootUrl) => {
    const byId = new Map();

    nodes.forEach((node) =>
        (node.relatedIndividuals || []).forEach((individual) => {
            const card = toIndividualCard(individual, rootUrl);
            if (card.primaryKey_ && !byId.has(card.primaryKey_)) {
                byId.set(card.primaryKey_, card);
            }
        })
    );

    return [...byId.values()].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '')
    );
};

const useCompanyOrgChart = (companyId, rootUrl = '') => {
    const { data, isLoading, error } = useSWR(
        companyId ? ['connections-v2-org-chart', companyId, rootUrl] : null,
        ([, holding, url]) =>
            fetchFenixOrgChart(holding).then((result) => {
                if (!result.ok) {
                    // Reject so SWR records the failure — resolving with an
                    // empty chart would render as "no organization data".
                    throw new Error('Org chart request failed');
                }
                const nodes = withCompanyUrls(result.nodes, url);
                return { nodes, individuals: individualsFromChart(nodes, url) };
            }),
        // No retry on error: with nothing cached, each retry flips isLoading
        // back on and the tabs oscillate between skeleton and error state.
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );

    return {
        orgChart: data?.nodes || [],
        individuals: data?.individuals || [],
        isLoading,
        error,
    };
};

export default useCompanyOrgChart;
